import { Fragment, useMemo } from 'react';

import { Chip, makeStyles } from '@material-ui/core';

import {
  RiAspectRatioLine,
  RiCursorLine,
  RiFireLine,
  RiFlashlightLine,
  RiInputMethodLine,
  RiScrollToBottomLine,
  RiTimeLine,
  RiZzzLine,
} from '@remixicon/react';
import type { eventWithTime } from 'rrweb';

import { SidebarSection } from '@spotify/confidence-core-react';
import {
  computeSessionStats,
  formatDuration,
} from '@spotify/confidence-session-replay-react';

import { StatRow } from './StatRow';

const useStyles = makeStyles({
  statList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-3)',
  },
  errorChip: {
    backgroundColor: 'var(--bui-bg-danger)',
    color: 'var(--bui-fg-danger)',
  },
});

type SessionSidebarProps = {
  events: eventWithTime[];
};

export const SessionSidebar = ({ events }: SessionSidebarProps) => {
  const classes = useStyles();
  const stats = useMemo(() => computeSessionStats(events), [events]);
  const totalInteractions =
    stats.totalClicks + stats.scrollEvents + stats.inputEvents;

  return (
    <Fragment>
      <SidebarSection.Root>
        <SidebarSection.Header>
          <SidebarSection.Title>Technical context</SidebarSection.Title>
        </SidebarSection.Header>
        <SidebarSection.Content>
          <div className={classes.statList}>
            {stats.viewport && (
              <StatRow
                icon={
                  <RiAspectRatioLine color="var(--bui-fg-disabled)" size={16} />
                }
                label="Viewport"
                value={stats.viewport}
              />
            )}
            <StatRow
              icon={<RiTimeLine color="var(--bui-fg-disabled)" size={16} />}
              label="Duration"
              value={formatDuration(stats.duration)}
            />
            <StatRow
              icon={
                <RiFlashlightLine color="var(--bui-fg-disabled)" size={16} />
              }
              label="Interactions"
              value={totalInteractions}
            />
          </div>
        </SidebarSection.Content>
      </SidebarSection.Root>

      <SidebarSection.Root>
        <SidebarSection.Header>
          <SidebarSection.Title>Behavioral summary</SidebarSection.Title>
        </SidebarSection.Header>
        <SidebarSection.Content>
          <div className={classes.statList}>
            <StatRow
              icon={<RiCursorLine color="var(--bui-fg-disabled)" size={16} />}
              label="Total clicks"
              value={stats.totalClicks}
            />
            <StatRow
              icon={<RiFireLine color="var(--bui-fg-disabled)" size={16} />}
              label="Rage clicks"
              value={
                stats.rageClicks > 0 ? (
                  <Chip
                    label={String(stats.rageClicks)}
                    size="small"
                    className={classes.errorChip}
                  />
                ) : (
                  '0'
                )
              }
            />
            <StatRow
              icon={
                <RiScrollToBottomLine
                  color="var(--bui-fg-disabled)"
                  size={16}
                />
              }
              label="Scroll events"
              value={stats.scrollEvents}
            />
            <StatRow
              icon={
                <RiInputMethodLine color="var(--bui-fg-disabled)" size={16} />
              }
              label="Input events"
              value={stats.inputEvents}
            />
            <StatRow
              icon={<RiZzzLine color="var(--bui-fg-disabled)" size={16} />}
              label="Idle time"
              value={formatDuration(stats.idleTime)}
            />
          </div>
        </SidebarSection.Content>
      </SidebarSection.Root>
    </Fragment>
  );
};
